import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import {
    List,
    Typography,
    Divider,
    Menu,
    Modal,
    Row, Col,
    Popover, Tooltip
} from 'antd';
import {FileCopyRounded, AddLinkRounded, PieChartRounded, AddCircleRounded,
    OpenInNewRounded, VisibilityOffRounded } from "@mui/icons-material";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import {selectFullPortfolios} from "../../../store/slices/portfolios.slice";
import Button from "@mui/material/Button";
import { EditNoteRounded, NoteAddRounded, AttachEmailRounded, ArrowUpwardRounded } from "@mui/icons-material";
import CopyText from "../../../components/Text/CopyText";
import { deletePages, listPageViews } from "../../../api/userApi";
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {RiMenuUnfold3Fill} from "@remixicon/react";
import {DeleteForeverRounded} from "@mui/icons-material";
import { BarChartRounded } from '@mui/icons-material';
import SearchableTable from "../../../components/Tables/SearchableTable";
import CardTabs from "../../../components/CardTabs";
import {AnalyticsOutlined, StackedLineChartOutlined, LeaderboardOutlined} from "@mui/icons-material";

const BANNER_ARTICLE_KEY = 'showpagecollectionbanner1';

const PortfolioList = ({ fullPortfolios }) => {
    // State to control showing all items
    const [showAll, setShowAll] = useState(false);

    // Determine how many portfolios to show initially
    const initialCount = 7;
    const portfoliosToShow = showAll ? Object.entries(fullPortfolios) : Object.entries(fullPortfolios).slice(0, initialCount);

    return (
        <div>
            <Tooltip title={'List of portfolios used across your IQ pages'}>
                <Typography.Title level={5} strong className={'mb-3'}>Portfolios ( {Object.entries(fullPortfolios).length} )</Typography.Title>
            </Tooltip>
            <List
                dataSource={portfoliosToShow}
                renderItem={([id, portfolio]) => (
                    <List.Item key={id} style={{ border: 'none', fontSize: '11px'}} className={'my-3 cursor'} onClick={()=>window.open(`/public/portfolios/${id}`)}>
                        {portfolio.name}
                    </List.Item>
                )}
            />

            {/* Show the "See More" button if there are more than 7 portfolios */}
            {Object.keys(fullPortfolios).length > initialCount && (
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Button
                        type="link"
                        style={{ color: '#000' }}
                        onClick={() => setShowAll(!showAll)}
                    >
                        {showAll ? "Show Less" : `Show ${Object.keys(fullPortfolios).length - initialCount} More`}
                    </Button>
                </div>
            )}
        </div>
    );
};



const PageMenu = ({handleRemove})=>{
    return (
        <Popover
            content={
            <Menu>
                <Menu.Item key="1" icon={<DeleteForeverRounded/>} onClick={handleRemove}>
                    Remove
                </Menu.Item>
            </Menu>
            }
        >
            <RiMenuUnfold3Fill size={16}/>
        </Popover>
    )
}

const PageCollection = ({ pageData, onEdit, onNewPage }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(localStorage.getItem(BANNER_ARTICLE_KEY) || 1);
  const [pagesWithConflicts, setPagesWithConflicts] = useState([]);
  const fullPortfolios = useSelector((state) => {
      const portfolios = selectFullPortfolios(state, [...new Set(pageData.flatMap(obj => obj.portfolio_ids).filter(String))]);
      return portfolios.reduce((acc, curr) => {
          acc[curr.id] = curr;  // Assume each portfolio has an 'id' and 'name' field
          return acc;
          }, {});
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Function to handle selection changes
  const onSelectChange = selectedKeys => {
    setSelectedRowKeys(selectedKeys);
  };


  // Function to confirm deletion
  const confirmDeletion = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete the selected pages?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      onOk() {
        dispatch(deletePages({ pageIds: selectedRowKeys }));
        setSelectedRowKeys([]);
      },
    });
  };


    const menu = <PageMenu handleRemove={confirmDeletion} />;
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnTitle:menu,
    };

    const handleListPageViews = async (pageId) => {
        // Dispatch listPageViews action to fetch the data
        await dispatch(listPageViews({ pageId }));

        // Navigate to the page view details route
        navigate(`/pages/${pageId}/views`);
    };
  const columns = [
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'title',
      render: (title, record)=>(
          <div className={'cursor'} onClick={()=>onEdit(pageData.find(obj=>obj.page_id===record.page_id))}>
            <Typography.Text>
              {title} <EditNoteRounded fontSize={'small'} />
            </Typography.Text>
          </div>
      ),
       searchable: true,
    },
    {
      title: "Link",
      dataIndex: 'id',
      key: 'id',
      render: (id, record)=><CopyText text={`${window.location.origin}/public/pages/${record.page_id}`} />,
    },
    {
      title: 'Visibility',
      dataIndex: 'is_public',
      key: 'is_public',
      render: (value) => (
          <Typography.Text style={{color: value === true ? 'red' : ''}}>{value===true?'Public':'Private'}</Typography.Text>
      ),
    },
    {
      title: 'Total Views',
      dataIndex: 'total_views',
      key: 'total_views',
      render: (value, record) => (
        value > 0 ?
        <Typography.Link onClick={() => handleListPageViews(record.page_id)}>
          {value} <OpenInNewRounded sx={{fontSize: '12px'}} />
        </Typography.Link>
        : <VisibilityOffRounded fontSize={'small'} />
      ),
    },

    {
      title: 'Portfolios',
      dataIndex: 'portfolio_ids',
      key: 'portfolio_ids',
      sorter: (a, b) => a.portfolio_ids.length - b.portfolio_ids.length,
      render: (portfolioIds) => (
        <Popover
          content={
            <List
              size="small"
              dataSource={portfolioIds.map(id => fullPortfolios[id]?.name || id)}
              renderItem={(name) => <List.Item>{name}</List.Item>}
            />
          }
          title="Portfolios"
          trigger="click"
        >
            <Button
              className={'px-3'}
              color={portfolioIds.length > 0 ? 'primary' : 'error'}
              variant={'text'}
              startIcon={<ArrowUpwardRounded fontSize={'small'}/>}
          >
                {portfolioIds.length}
          </Button>
        </Popover>
      ),
    },
    {
      title: 'Files',
      dataIndex: 'files',
      key: 'files',
      render: (files) => (
        <Popover
          content={
            <List
              size="small"
              dataSource={files}
              renderItem={(name) => <List.Item>{name}</List.Item>}
            />
          }
          title="Files"
          trigger="click"
        >
          <Button
              className={'px-3'}
              color={files.length > 0 ? 'primary' : 'inherit'}
              variant={'text'}
              startIcon={<NoteAddRounded fontSize={'small'}/>}
              label={files.length}

          >
                {files.length}
          </Button>
        </Popover>
      ),
    },
    {
      title: 'Emails',
      dataIndex: 'emails',
      key: 'emails',
      render: (emails, record) => (
        <Popover
          content={
            <List
              size="small"
              dataSource={emails}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          }
          title="Emails"
          trigger="click"
        >
            <Button
              className={'px-3'}
              color={emails.length > 0 ? 'primary' : record.is_public ? 'inherit' : 'error'}
              variant={'text'}
              startIcon={<AttachEmailRounded fontSize={'small'}/>}
          >
                {emails.length}
          </Button>
        </Popover>
      ),
    },
  ];

  const totalViews = useMemo(()=>{
      let total = 0;
      pageData.forEach(page=>{
          total += page.total_views;
      });
        return total;
  }, [pageData]);

  useEffect(() => {
    const privatePortfoliosWithNoEmails = pageData.filter(page => !page.is_public && page.emails.length === 0).map(page => page.title);
    setPagesWithConflicts(privatePortfoliosWithNoEmails);
  }, [fullPortfolios]);


  const statItems = [
      {IconComponent: AnalyticsOutlined, title: `${pageData.length} Pages`, description: "Total IQ Pages youve created", color: 'var(--green)'},
        {IconComponent: BarChartRounded, title: `${totalViews} Views`, description: "Total views across all pages", color: 'var(--red)'},
        {IconComponent: AddLinkRounded, title: `${Object.keys(fullPortfolios).length} Portfolios`, description: "Unique Portfolios in IQ pages", color: 'var(--blue)'}
  ]

  return (
      <Row gutter={64} wrap={false}>
          <Col flex={'0 0 250px'} className={'py-3 bordered round-2'}>
              <PortfolioList fullPortfolios={fullPortfolios} />
          </Col>
          <Col flex={'1'}>
              {showBanner === 1 && (
                  <>
                    <Alert className={'m-3'} onClose={()=>{localStorage.setItem(BANNER_ARTICLE_KEY, 0);setShowBanner(0)}}>
                          <AlertTitle>Introducing <b>Pages&trade;</b> </AlertTitle>
                          IQ is our new way for you to engage others with personalized content including portfolios, files, and other
                          resources you might want to include
                      </Alert>
                      <Divider/>
                  </>
              )}
              <Row align={'middle'} justify={'space-between'} style={{paddingBottom: 30}} wrap={false}>
                  <Col>
                      <Button onClick={onNewPage} startIcon={<AddCircleRounded/>}  color={'info'} variant={'contained'} size={'large'}>Create New Page</Button>

                  </Col>
                  <Col>
                      <CardTabs items={statItems} width={280} />

                  </Col>
                  {/*<Space size={'large'}>*/}
                  {/*    <Stat title={'Total Pages'} icon={FileCopyRounded} value={pageData.length} imgUrl={'https://storage.googleapis.com/finiat-web-static-resources/dramatic/number-raindrops.jpeg'} />*/}
                  {/*    <Stat title={'Total Views'} icon={BarChartRounded} value={totalViews} imgUrl={'https://cdn.openai.com/API/images/gradient_card_1.webp'} />*/}
                  {/*    <Stat title={'Portfolios'} icon={AddLinkRounded} value={Object.keys(fullPortfolios).length} imgUrl={'https://storage.googleapis.com/finiat-web-static-resources/NEWEST-7-9-2024/purple-globe.png'} />*/}
                  {/*</Space>*/}
              </Row>
              {pagesWithConflicts.length > 0 && (
                  <Alert severity="warning" className={'m-3'}>
                    <AlertTitle>Warning</AlertTitle>
                      {pagesWithConflicts.length} do not have emails associated with them and are set as private, they cannot currently be viewed.
                      {pagesWithConflicts.map(x=>`[ ${x} ]`).join(' ')}
                  </Alert>
                )}
              <SearchableTable
                dataSource={pageData}
                columns={columns}
                rowKey="page_id"
                rowSelection={rowSelection}
                pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '30', '50', '100', '250'],

                }}
            />
          </Col>
      </Row>
  );
};

export default PageCollection;